import React, { useEffect } from 'react';
import { Navigate, Outlet, Path, useLocation } from 'react-router-dom';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { appRoutes, RouteObject } from './routes.types';
import { useAuthStore } from 'src/services/Auth/Auth.store';
import { usePreLoginAuthLocation } from 'src/routes/use-pre-login-auth-location';
import { useSaveHubspotUtmLocationEffect } from 'src/lib/services/hubspot/use-hubspot-saved-utm-location';
import { useAuth } from 'src/services/Auth';
import { useProfile } from 'src/models';
import { useBrandsQuery } from 'src/api/queries';
import { usePendingPurchaseEvents } from 'src/hooks/use-pending-purchase-events';
import { FullPageSpinner } from 'src/components/Spinner';
import { isMobileAppOnboardingAllowed, isReactNativeWebView } from 'src/utils/webview-detection';
import {
  ExpiredWindowGate,
  isExpiredWindowAllowedPath,
  useIsExpiredShell,
} from 'src/features/expired-window';
import { LazyAuthenticatedLayout } from 'src/components/ui/layouts/authenticated-layout/lazy-authenticated-layout';

type ProtectedRouteProps = {
  route: RouteObject;
};

// TODO: This component should only be rendered once per route.
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ route }) => {
  const location = useLocation();
  const { isLoggedIn } = useAuthStore();
  const { rememberLocation } = usePreLoginAuthLocation();
  const { refresh } = useAuth();
  // Enable profile query when user is logged in - this is critical for redirect logic
  const profile = useProfile({ enabled: isLoggedIn });
  const isExpiredShell = useIsExpiredShell();
  const brandsQuery = useBrandsQuery();
  const hasBrands = !!brandsQuery?.data?.items?.length;

  useSaveHubspotUtmLocationEffect();

  // Process pending purchase events when user is authenticated
  usePendingPurchaseEvents(isLoggedIn);

  useEffect(() => {
    // Invoke refresh token every hour
    const tokenRefreshInvertal = setInterval(() => refresh(), 60 * 60 * 1000);

    return () => {
      clearInterval(tokenRefreshInvertal);
    };
  }, [refresh]);

  // Wait for initial data to load before evaluating any redirect conditions
  const isInitiallyLoading =
    (isLoggedIn && !profile.isLoaded) ||
    (isLoggedIn && profile.query.isLoading) ||
    (isLoggedIn && brandsQuery.isLoading && !brandsQuery.isFetched);

  if (route.underMaintenance) {
    return <Navigate to={appRoutes.maintenance} />;
  }

  // Wait for queries to load, but proceed if they error out to avoid trapping users
  if (isInitiallyLoading && !profile.query.isError && !brandsQuery.isError) {
    return (
      <route.layout>
        {/* WebView only: desktop keeps the empty layout it always showed while this loads. */}
        {isReactNativeWebView() ? <FullPageSpinner /> : <div />}
      </route.layout>
    );
  }

  // Redirect non-logged-in users to login page
  if (!isLoggedIn && route.restricted?.includes('guest')) {
    if (
      location.pathname === appRoutes.addSubscriptionPlanSelect ||
      location.pathname === appRoutes.stripePaymentLinkRedirect
    ) {
      return (
        <Navigate to={appRoutes.register} state={{ from: rememberLocation(location) }} replace />
      );
    }
    return <Navigate to={appRoutes.login} state={{ from: rememberLocation(location) }} replace />;
  }

  // Check if this is mobile app onboarding flow (allows bypassing subscription)
  const isMobileOnboardingRoute = location.pathname === appRoutes.mobileOnboardingCreateBrand;
  const isMobileRequestEditRoute = location.pathname === appRoutes.mobileRequestEdit;
  const isMobileAppAllowed =
    (isMobileOnboardingRoute && isMobileAppOnboardingAllowed()) || isMobileRequestEditRoute;

  // A null access state means the back end has the Expired window switched off:
  // the pricing-table and /account-on-hold redirects below then work as before.
  const isExpiredWindowEnabled = profile.accessState !== null;

  // Expired window (#2779): a company that lost access sees it in place of any
  // product page, on the URL it asked for, so the link works again the moment it
  // reactivates. The avatar-menu pages stay reachable. Impersonating admins pass.
  // Read from the last answer even when a refetch has since failed, so a dropped
  // connection never reveals the product (isLoaded turns false on error).
  if (
    !profile.query.isLoading &&
    isExpiredShell &&
    !isMobileAppAllowed &&
    route.restricted?.includes('guest') &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect &&
    location.pathname !== appRoutes.authVerifyEmail
  ) {
    if (!isExpiredWindowAllowedPath(location.pathname)) {
      return (
        <LazyAuthenticatedLayout>
          <ExpiredWindowGate />
        </LazyAuthenticatedLayout>
      );
    }

    return (
      <route.layout>
        <Outlet />
      </route.layout>
    );
  }

  // Redirect users without active subscriptions to subscription page (with the
  // Expired window on: only a company that never completed a purchase)
  // Exception: Mobile app onboarding flow and impersonated users are allowed to bypass this
  if (
    profile.isLoaded &&
    !profile.query.isLoading &&
    (isExpiredWindowEnabled
      ? profile.accessState === 'pricing_table'
      : !profile.hasActiveSubscriptions) &&
    !profile.isImpersonated &&
    !isMobileAppAllowed &&
    location.pathname !== appRoutes.addSubscriptionPlanSelect &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect
  ) {
    return <Navigate to={appRoutes.addSubscriptionPlanSelect} state={{ from: location }} replace />;
  }

  // Force brand creation for new users (no brands AND no requests)
  // Users with requests but no brands can access dashboard (edge case: deleted brand)
  //
  // `!brandsQuery.isError` is load-bearing, not defensive noise. `hasBrands` reads
  // `data?.items?.length`, which an ERRORED query leaves undefined — and `isFetched` is true
  // after a failure — so without it a failed brands request is indistinguishable from "this
  // customer owns no brands", and a paying customer with brands gets sent into first-brand
  // onboarding on one transient 401/429. "We don't know" is not "none". Line 54 already
  // exempts isError from the loading gate for the same reason; this branch was the one place
  // that still conflated them.
  if (
    profile.isLoaded &&
    !brandsQuery.isLoading &&
    brandsQuery.isFetched &&
    !brandsQuery.isError &&
    profile.hasActiveSubscriptions &&
    !hasBrands &&
    !profile.hasRequests &&
    location.pathname !== appRoutes.createBrand &&
    location.pathname !== appRoutes.dashboard &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect &&
    location.pathname !== appRoutes.mobileRequestEdit
  ) {
    return <Navigate to={appRoutes.createBrand} state={{ from: location }} replace />;
  }

  if (isLoggedIn) {
    const userType = AuthUtils.getPayload()?.user_type;

    // Redirect logged-in users away from restricted pages (login/register).
    // An expired company goes too: nothing else moves it off the login page
    // once it no longer gets the pricing redirect, so without brands or
    // requests it would sit on "Welcome back" after logging in.
    const isExpired = profile.accessState === 'expired';
    if (
      route.restricted?.includes(userType as any) &&
      (hasBrands || profile.hasRequests || isExpired)
    ) {
      // In mobile WebView, redirect to AI agent instead of dashboard
      const defaultRoute = isReactNativeWebView() ? appRoutes.aiAgent : appRoutes.dashboard;
      // The expired client goes back to the link it asked for, so the window
      // shows on that URL (or the allowed page itself opens)
      const from = (location.state as { from?: Partial<Path> } | null)?.from;
      const target = isExpired && from?.pathname ? from : defaultRoute;
      return <Navigate to={target} replace />;
    }

    // Redirect users with failed subscription payments to account hold page
    // Exception: Impersonated users should bypass this to allow admins to view the account
    // Retired by the Expired window; kept only while it is switched off (rollback)
    if (
      !isExpiredWindowEnabled &&
      profile.isLoaded &&
      !profile.query.isLoading &&
      !profile.isImpersonated &&
      !profile.eligibleForTrial &&
      !profile.hasActiveSubscriptions &&
      profile.hasFailedSubscriptions &&
      location.pathname !== appRoutes.accountOnHold &&
      location.pathname !== appRoutes.addSubscriptionPlanSelect &&
      !location.pathname.includes('/subscription-cancelation/subscription')
    ) {
      return <Navigate to={appRoutes.accountOnHold} replace />;
    }

    // Redirect from account hold page to dashboard if subscription is active
    if (
      profile.isLoaded &&
      !profile.query.isLoading &&
      !brandsQuery.isLoading &&
      location.pathname === appRoutes.accountOnHold &&
      // With the Expired window on, an expired company never gets this far
      (isExpiredWindowEnabled || (!profile.hasFailedSubscriptions && hasBrands))
    ) {
      const defaultRoute = isReactNativeWebView() ? appRoutes.aiAgent : appRoutes.dashboard;
      return <Navigate to={defaultRoute} replace />;
    }
  }

  return (
    <route.layout>
      <Outlet />
    </route.layout>
  );
};
